import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { Calendar, ChevronLeft, ChevronRight, Clock, X } from "lucide-react";
import SelectMenu from "./SelectMenu";
import { FIELD_ERROR, FIELD_LABEL } from "./formFieldStyles";

// Drop-in replacement for <input type="datetime-local">. The native control
// renders as cramped mm/dd/yyyy segments that differ per browser; this keeps the
// same value format ("YYYY-MM-DDTHH:mm") and the same onChange event shape, so
// callers only swap the component.
const VALUE_FORMAT = "YYYY-MM-DDTHH:mm";
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

const pad = (n) => String(n).padStart(2, "0");
const HOUR_OPTIONS = HOURS.map((h) => ({ value: h, label: pad(h) }));
const MINUTE_OPTIONS = MINUTES.map((m) => ({ value: m, label: pad(m) }));
const TIME_SELECT_CLASS =
  "text-[13px] rounded-lg border border-gray-200 bg-white pl-2 pr-7 py-1 outline-none focus:border-brand-500";

const DateTimeField = ({
  label,
  name,
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  withTime = true,
  min,
  placeholder,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  const selected = useMemo(() => {
    if (!value) return null;
    const d = dayjs(value);
    return d.isValid() ? d : null;
  }, [value]);

  const minDate = useMemo(() => {
    if (!min) return null;
    const d = dayjs(min);
    return d.isValid() ? d.startOf("day") : null;
  }, [min]);

  const [viewMonth, setViewMonth] = useState(() =>
    (selected || dayjs()).startOf("month")
  );

  // Re-centre the grid on the selected month each time the popover opens.
  useEffect(() => {
    if (open) setViewMonth((selected || dayjs()).startOf("month"));
  }, [open, selected]);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e) => {
      // The hour/minute lists are portalled to <body>, outside this container.
      if (e.target.closest?.("[data-select-menu]")) return;
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const emit = (next) =>
    onChange?.({
      target: { name, value: next ? next.format(VALUE_FORMAT) : "" },
    });

  const openPicker = () => {
    if (disabled) return;
    // Always open below; bring a field near the bottom edge up so it fits.
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!open && rect && window.innerHeight - rect.bottom < 380) {
      triggerRef.current.scrollIntoView({ block: "center" });
    }
    setOpen((prev) => !prev);
  };

  const days = useMemo(() => {
    const gridStart = viewMonth.startOf("month").startOf("week");
    return Array.from({ length: 42 }, (_, i) => gridStart.add(i, "day"));
  }, [viewMonth]);

  const isDisabledDay = (day) => minDate && day.isBefore(minDate, "day");

  const pickDay = (day) => {
    if (isDisabledDay(day)) return;
    // Keep the time already chosen; a fresh value starts at 09:00.
    const base = selected || dayjs().hour(9).minute(0);
    emit(day.hour(withTime ? base.hour() : 0).minute(withTime ? base.minute() : 0));
    if (!withTime) setOpen(false);
  };

  // Time edits before a day is picked land on today.
  const withBase = (mutate) => emit(mutate(selected || dayjs().minute(0)));

  const hour12 = selected ? selected.hour() % 12 || 12 : 12;
  const meridiem = selected && selected.hour() >= 12 ? "PM" : "AM";

  const setHour12 = (h) =>
    withBase((d) => d.hour(meridiem === "PM" ? (h % 12) + 12 : h % 12));

  const setMeridiem = (m) => {
    if (m === meridiem) return;
    withBase((d) => d.hour(m === "PM" ? (d.hour() % 12) + 12 : d.hour() % 12));
  };

  const displayValue = selected
    ? selected.format(withTime ? "DD MMM YYYY, hh:mm A" : "DD MMM YYYY")
    : "";

  const stateClasses = error
    ? "border-danger-300 hover:border-danger-400"
    : "border-gray-200 hover:border-gray-300";

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className={FIELD_LABEL}>
          {label} {required && <span className="text-danger-500">*</span>}
        </label>
      )}

      <button
        ref={triggerRef}
        type="button"
        onClick={openPicker}
        disabled={disabled}
        className={`flex w-full items-center justify-between gap-2 text-[13px] rounded-lg border bg-white px-3 py-2 text-left outline-none transition-colors
          ${stateClasses}
          ${open ? "border-brand-500 ring-2 ring-brand-100" : ""}
          ${disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "text-gray-900"}
        `}
      >
        <span className={displayValue ? "" : "text-gray-400"}>
          {displayValue || placeholder || (withTime ? "Select date & time" : "Select date")}
        </span>
        <Calendar className="w-4 h-4 shrink-0 text-gray-400" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-[288px] rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => setViewMonth((m) => m.subtract(1, "month"))}
              className="p-1 rounded hover:bg-gray-100 text-gray-500"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-semibold text-gray-900">
              {viewMonth.format("MMMM YYYY")}
            </span>
            <button
              type="button"
              onClick={() => setViewMonth((m) => m.add(1, "month"))}
              className="p-1 rounded hover:bg-gray-100 text-gray-500"
              aria-label="Next month"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-7 mb-1">
            {WEEKDAYS.map((d) => (
              <span
                key={d}
                className="text-center text-[10px] font-semibold uppercase tracking-wide text-gray-400 py-1"
              >
                {d}
              </span>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {days.map((day) => {
              const outside = day.month() !== viewMonth.month();
              const isSelected = selected && day.isSame(selected, "day");
              const isToday = day.isSame(dayjs(), "day");
              const unavailable = isDisabledDay(day);

              let tone = "text-gray-700 hover:bg-brand-50";
              if (outside) tone = "text-gray-300 hover:bg-gray-50";
              if (isToday && !isSelected) tone = "text-brand-600 font-semibold hover:bg-brand-50";
              if (isSelected) tone = "bg-brand-600 text-white font-semibold hover:bg-brand-600";
              if (unavailable) tone = "text-gray-200 cursor-not-allowed";

              return (
                <button
                  key={day.format("YYYY-MM-DD")}
                  type="button"
                  onClick={() => pickDay(day)}
                  disabled={unavailable}
                  className={`h-8 rounded text-xs transition-colors ${tone}`}
                >
                  {day.date()}
                </button>
              );
            })}
          </div>

          {withTime && (
            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-400 shrink-0" />

              <div className="w-16">
                <SelectMenu
                  value={hour12}
                  onChange={(e) => setHour12(Number(e.target.value))}
                  options={HOUR_OPTIONS}
                  searchable={false}
                  menuMinWidth={64}
                  className={TIME_SELECT_CLASS}
                />
              </div>

              <span className="text-gray-400">:</span>

              <div className="w-16">
                <SelectMenu
                  value={selected ? selected.minute() : 0}
                  onChange={(e) => withBase((d) => d.minute(Number(e.target.value)))}
                  options={MINUTE_OPTIONS}
                  searchable={false}
                  menuMinWidth={64}
                  className={TIME_SELECT_CLASS}
                />
              </div>

              <div className="ml-auto flex rounded-md border border-gray-200 overflow-hidden">
                {["AM", "PM"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMeridiem(m)}
                    className={`px-2 py-1 text-xs font-medium transition-colors ${
                      meridiem === m
                        ? "bg-brand-600 text-white"
                        : "bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={() => {
                const now = dayjs();
                emit(minDate && now.isBefore(minDate, "day") ? minDate.hour(9) : now);
              }}
              className="font-medium text-brand-600 hover:text-brand-800"
            >
              {withTime ? "Now" : "Today"}
            </button>

            <div className="flex items-center gap-3">
              {selected && (
                <button
                  type="button"
                  onClick={() => emit(null)}
                  className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-700"
                >
                  <X className="w-3 h-3" />
                  Clear
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-2.5 py-1 font-medium text-white bg-brand-600 hover:bg-brand-700 rounded"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {error && <p className={FIELD_ERROR}>{error}</p>}
    </div>
  );
};

export default DateTimeField;
